import React, { useContext, useEffect, useState } from 'react'
import { useBlocker } from 'react-router';
import { TMerchantSettings } from '../Types';
import { getMerchantSettings, updateMerchantSettings } from '../ARSServices/ARSServices';
import Button from 'Components/DesignSystem/Button/Button';
import Spinner from 'Components/DesignSystem/Spinner/Spinner';
import NoDataComponent from 'Components/Common/NoDataComponent';
import Toggle from 'Components/DesignSystem/Toggle/Toggle';
import FieldTitle from 'Components/DesignSystem/FieldTitle/FieldTitle';
import Dropdown from 'Components/DesignSystem/Dropdown/Dropdown';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import ConfirmModal from 'Pages/GMS/ISR/Components/RuleComponents/CommitConfirmModal';
import { AppContext } from 'Context/AppContext';
import { CopyIcon } from 'assests/GmsIcons';
import OverlayIcon from 'Components/GMS/Rules/Components/OverlayIcon';

type MerchantSettingsModalProps = {
    showMerchantSettings: boolean;
    closeMerchantSettings: () => void;
    channelId: string;
    environmentKey?: string;
    id?: string;
};

const COMPONENT_ID = 'merchant-settings';

const MerchantSettingsModal = ({ showMerchantSettings, closeMerchantSettings, channelId, environmentKey = '', id }: MerchantSettingsModalProps) => {

    const appContext = useContext(AppContext);
    const [merchantSettings, setMerchantSettings] = useState<TMerchantSettings | undefined>(undefined);
    const [originalSettings, setOriginalSettings] = useState<TMerchantSettings | undefined>(undefined);
    const [loading, setLoading] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState<boolean>(false);

    const hasChanges = JSON.stringify(merchantSettings) !== JSON.stringify(originalSettings);

    // Browser refresh/close protection
    useEffect(() => {
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            if (hasChanges) {
                event.preventDefault();
                event.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [hasChanges]);

    // SPA route navigation protection
    const blocker = useBlocker(hasChanges);

    useEffect(() => {
        if (blocker.state === "blocked") {
            setShowUnsavedChangesModal(true);
        }
    }, [blocker.state]);

    useEffect(() => {
        if (showMerchantSettings) {
            fetchMerchantSettingsData();
            appContext?.setEditInProgress(true);
        } else {
            appContext?.setEditInProgress(false);
        }

        return () => {
            appContext?.setEditInProgress(false);
        };
    }, [showMerchantSettings]);

    const menuClasses = showMerchantSettings
        ? 'tw-absolute tw-top-0 tw-right-0 tw-h-full tw-max-h-full tw-overflow-auto tw-w-full md:tw-w-[550px] tw-bg-white tw-z-50 tw-shadow-lg'
        : 'tw-hidden';

    const applyDefaultSettings = (data: Partial<TMerchantSettings>): TMerchantSettings => ({
        ...data,
        check_availability: data.check_availability ?? false,
        exclude_base_product_variants: data.exclude_base_product_variants ?? false,
        pls_filters: data.pls_filters ?? false,
        rec_scoring_method: data.rec_scoring_method ?? 'Combined'
    } as TMerchantSettings);

    const fetchMerchantSettingsData = async () => {
        if (merchantSettings != null) return;
        setLoading(true);

        try {
            const res = await getMerchantSettings(channelId);

            if (res?.done) {
                const settingsWithDefaults = applyDefaultSettings(res.data);
                setMerchantSettings(settingsWithDefaults);
                setOriginalSettings(settingsWithDefaults);
            }
        } catch (error) {
            console.error(`Error fetching data:`, error);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (merchantSettings == null) return;
        setSaving(true);

        try {
            const res = await updateMerchantSettings(channelId, merchantSettings);

            if (res?.done) {
                const settingsWithDefaults = applyDefaultSettings(res.data);
                setMerchantSettings(settingsWithDefaults);
                setOriginalSettings(settingsWithDefaults);
                toast.success('Merchant settings saved successfully');
                closeMerchantSettings();
            }
        } catch (error) {
            console.error(`Error saving data:`, error);
            toast.error('Failed to save merchant settings');
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        if (hasChanges) {
            setShowUnsavedChangesModal(true);
        } else {
            closeMerchantSettings();
        }
    };

    const handleModalClose = () => {
        setShowUnsavedChangesModal(false);
        if (blocker.state === "blocked") blocker.reset();
    };

    const handleModalSave = async () => {
        await handleSave();
        setShowUnsavedChangesModal(false);
        if (blocker.state === "blocked") {
            blocker.proceed();
        } else {
            closeMerchantSettings();
        }
    };

    const handleModalDiscard = () => {
        setMerchantSettings(originalSettings);
        setShowUnsavedChangesModal(false);
        if (blocker.state === "blocked") {
            blocker.proceed();
        } else {
            closeMerchantSettings();
        }
    };

    useEffect(() => {
        if (showMerchantSettings) {
            fetchMerchantSettingsData();
        }
    }, [showMerchantSettings, channelId]);

    return (
        <div id={id} data-testid={COMPONENT_ID} className="tw-h-[calc(100vh-58px)] tw-fixed tw-top-0 tw-left-0 tw-w-full tw-z-40 tw-mt-14">
            <div className={menuClasses}>
                <div className={`tw-border-b tw-pt-[22px] tw-pr-2 tw-pl-2 tw-sticky tw-top-0 tw-bg-white tw-z-10`}>
                    <div className="tw-flex tw-justify-between tw-items-center tw-pb-0.5">
                        <div className="tw-flex tw-items-center tw-gap-1.5 tw-ml-2">
                            <span className="tw-font-bold">Settings</span>
                            <OverlayIcon message="Settings apply to all the models in this channel-environment" placement="bottom" />
                        </div>
                        <div className="tw-flex tw-gap-2 tw-shrink-0">
                            <Button
                                data-testid={`${COMPONENT_ID}-cancel-btn`}
                                variant="secondary"
                                onClick={handleCancel}
                                disabled={loading}
                            >
                                Cancel
                            </Button>
                            <Button
                                data-testid={`${COMPONENT_ID}-save-btn`}
                                variant="primary"
                                onClick={handleSave}
                                disabled={loading || saving || !hasChanges}
                            >
                                {saving ? 'Saving...' : 'Save'}
                            </Button>
                        </div>
                    </div>
                    <div className="tw-flex tw-items-center tw-gap-2 tw-ml-2 tw-pb-2">
                        <span className="tw-text-sm tw-text-[#3D4966]">Environment Key</span>
                        <button data-testid="components-merchantsettingsmodal-button-1"
                            className="tw-text-[#0052cc] tw-flex tw-items-center"
                            onClick={() => {
                                if (navigator.clipboard && navigator.clipboard.writeText) {
                                    navigator.clipboard.writeText(environmentKey);
                                } else {
                                    const textarea = document.createElement('textarea');
                                    textarea.value = environmentKey;
                                    textarea.style.position = 'fixed';
                                    textarea.style.opacity = '0';
                                    document.body.appendChild(textarea);
                                    textarea.focus();
                                    textarea.select();
                                    // @ts-ignore - execCommand is the only fallback for iOS Safari < 13.4
                                    document.execCommand('copy');
                                    document.body.removeChild(textarea);
                                }
                                toast.success('Environment key copied to clipboard');
                            }}
                            title="Copy environment key"
                        >
                            <CopyIcon />
                        </button>
                    </div>
                </div>

                <div className="tw-p-4">
                    {loading ? (
                        <div className="tw-flex tw-justify-center tw-py-8">
                            <Spinner size="md" label="Loading settings" showLabel data-testid="merchant-settings-loading" />
                        </div>
                    ) : merchantSettings ? (
                        <>
                            <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
                                <FieldTitle label="Check Availability" infoMessage={merchantSettings.availability_info_message}></FieldTitle>
                                <Toggle
                                    data-testid={`${COMPONENT_ID}-check-availability-toggle`}
                                    label=""
                                    checked={merchantSettings.check_availability}
                                    onChange={(checked) => setMerchantSettings(prev =>
                                        prev ? { ...prev, check_availability: checked, pls_filters: checked ? false : prev.pls_filters } : prev
                                    )}
                                />
                            </div>
                            <div className="tw-flex tw-items-center tw-justify-between">
                                <FieldTitle label="Check Product Lifecycle Status" infoMessage={merchantSettings.pls_info_message}></FieldTitle>
                                <Toggle
                                    data-testid={`${COMPONENT_ID}-check-pls-toggle`}
                                    label=""
                                    checked={merchantSettings.pls_filters}
                                    onChange={(checked) => setMerchantSettings(prev =>
                                        prev ? { ...prev, pls_filters: checked, check_availability: checked ? false : prev.check_availability } : prev
                                    )}
                                />
                            </div>
                            <div className={`tw-box-border tw-w-auto tw-h-0 tw-mr-3 tw-border-b-[2px] tw-my-4 tw-border-solid tw-border-grey-200`} />
                            <div className="tw-flex">
                                <Dropdown
                                    data-testid={`${COMPONENT_ID}-scoring-dropdown`}
                                    label="Recommendation Scoring"
                                    infoMessage={merchantSettings.scoring_method_info_message}
                                    value={merchantSettings.rec_scoring_method}
                                    onChange={(option) => setMerchantSettings(prev =>
                                        prev ? { ...prev, rec_scoring_method: String(option) } : prev
                                    )}
                                    options={merchantSettings.scoring_methods.map(scoring_method => ({
                                        value: scoring_method.method,
                                        label: scoring_method.method,
                                        tooltip: scoring_method.info_message
                                    }))}
                                />
                            </div>
                            <div className={`tw-box-border tw-w-auto tw-h-0 tw-mr-3 tw-border-b-[2px] tw-my-4 tw-border-solid tw-border-grey-200`} />
                            <div className="tw-flex tw-items-center tw-justify-between">
                                <FieldTitle label="Exclude Base Product Variants" infoMessage={merchantSettings.exclude_variants_info_message}></FieldTitle>
                                <Toggle
                                    data-testid={`${COMPONENT_ID}-top-variant-toggle`}
                                    label=""
                                    checked={merchantSettings.exclude_base_product_variants}
                                    onChange={(checked) => setMerchantSettings(prev =>
                                        prev ? { ...prev, exclude_base_product_variants: checked } : prev
                                    )}
                                />
                            </div>
                            <div className={`tw-box-border tw-w-auto tw-h-0 tw-mr-3 tw-border-b-[2px] tw-my-4 tw-border-solid tw-border-grey-200`} />
                        </>

                    ) : (
                        <NoDataComponent message={`No settings found. Please close and reopen merchant settings`} />
                    )}
                </div>
            </div>

            {(showUnsavedChangesModal || blocker.state === "blocked") && (
                <ConfirmModal
                    id={`${COMPONENT_ID}-unsaved-changes-modal`}
                    title="Unsaved Changes"
                    message="You have unsaved changes to merchant settings. What would you like to do?"
                    onClose={handleModalClose}
                    onConfirm={handleModalSave}
                    ternaryMethod={handleModalDiscard}
                    ternaryText="Discard Changes"
                    confirmText="Save Changes"
                    cancelText="Cancel"
                    saveInProgress={saving}
                />
            )}
        </div>
    );
};

export default MerchantSettingsModal